import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { HeaderPage } from "@/components/HeaderPage";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
  updateUserSchema,
  type UpdateUserFormData,
} from "@/schemas/userSchemas";
import { useGetUserById, useUpdateUser } from "@/hooks/useUsers";
import { Loading } from "@/components/Loading";
import { useEffect } from "react";

export const EditUser = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading } = useGetUserById(id!);
  const updateUser = useUpdateUser();

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = useForm<UpdateUserFormData>({
    resolver: zodResolver(updateUserSchema),
  });

  useEffect(() => {
    if (user) {
      // Formatar CPF
      const formattedCpf = user.profile?.cpf
        ? user.profile.cpf
            .replace(/\D/g, "")
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d{1,2})/, "$1-$2")
            .replace(/(-\d{2})\d+?$/, "$1")
        : "";

      // Formatar Telefone
      const formattedPhone = user.profile?.phone
        ? user.profile.phone
            .replace(/\D/g, "")
            .replace(/(\d{2})(\d)/, "($1) $2")
            .replace(/(\d{5})(\d)/, "$1-$2")
            .replace(/(-\d{4})\d+?$/, "$1")
        : "";

      // Formatar CEP
      const formattedZipCode = user.profile?.zipCode
        ? user.profile.zipCode
            .replace(/\D/g, "")
            .replace(/(\d{5})(\d)/, "$1-$2")
            .replace(/(-\d{3})\d+?$/, "$1")
        : "";

      const formData = {
        role: user.role as "ADMIN" | "AGENT" | "CITIZEN" | "AUDIT" | "TRIAGE",
        isActive: user.isActive ?? false,
        fullName: user.profile?.fullName || "",
        cpf: formattedCpf,
        phone: formattedPhone,
        street: user.profile?.street || "",
        number: user.profile?.number || "",
        neighborhood: user.profile?.neighborhood || "",
        city: user.profile?.city || "",
        state: user.profile?.state || "",
        zipCode: formattedZipCode,
      };

      reset(formData);
    }
  }, [user, reset]);

  const onSubmit = async (data: UpdateUserFormData) => {
    try {
      await updateUser.mutateAsync({
        userId: id!,
        role: data.role,
        isActive: data.isActive,
        fullName: data.fullName,
        cpf: data.cpf.replace(/\D/g, ""),
        phone: data.phone.replace(/\D/g, ""),
        street: data.street,
        number: data.number,
        neighborhood: data.neighborhood,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode.replace(/\D/g, ""),
      });
      navigate("/users");
      toast.success("Usuário atualizado com sucesso!");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Erro ao atualizar usuário";
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return <Loading label="Carregando usuário..." />;
  }

  if (!user) {
    return (
      <div>
        <HeaderPage
          title="Editar usuário"
          description="Atualize as informações do usuário"
        />
        <div className="flex-1 overflow-y-auto mt-10">
          <Card className="bg-white">
            <CardContentUI className="p-6">
              <p className="text-center text-gray-500">
                Usuário não encontrado
              </p>
            </CardContentUI>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <HeaderPage
        title="Editar Usuário"
        description="Atualize as informações do usuário"
      />

      <div className="flex-1 overflow-y-auto mt-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-white">
            <CardContentUI className="p-6">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Informações da Conta
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input title="Email" disabled value={user?.email || ""} />

                    <Controller
                      name="isActive"
                      control={control}
                      render={({ field }) => (
                        <div className="flex flex-col gap-1.5">
                          <Label className="text-sm font-medium">
                            Status da Conta
                          </Label>
                          <div
                            className={`flex items-center gap-3 px-3 py-2 rounded-md border ${
                              field.value
                                ? "border-green-200 bg-green-50"
                                : "border-red-200 bg-red-50"
                            }`}
                          >
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="cursor-pointer"
                            />
                            <span
                              className={`text-sm font-medium ${
                                field.value ? "text-green-700" : "text-red-600"
                              }`}
                            >
                              {field.value ? "Ativo" : "Inativo"}
                            </span>
                          </div>
                        </div>
                      )}
                    />

                    <Controller
                      name="role"
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || user?.role}
                        >
                          <SelectTrigger title="Função" isRequired>
                            <SelectValue placeholder="Selecione a função" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="CITIZEN">Cidadão</SelectItem>
                            <SelectItem value="AGENT">
                              Agente de Campo
                            </SelectItem>
                            <SelectItem value="ADMIN">Administrador</SelectItem>
                            <SelectItem value="TRIAGE">Triagem</SelectItem>
                            <SelectItem value="AUDIT">Auditoria</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Informações Pessoais
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      title="Nome Completo"
                      isRequired
                      placeholder="João Silva Santos"
                      error={errors.fullName?.message}
                      {...register("fullName")}
                    />

                    <Controller
                      name="cpf"
                      control={control}
                      render={({ field }) => (
                        <Input
                          title="CPF"
                          isRequired
                          placeholder="000.000.000-00"
                          error={errors.cpf?.message}
                          value={field.value}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            const maskedValue = value
                              .replace(/(\d{3})(\d)/, "$1.$2")
                              .replace(/(\d{3})(\d)/, "$1.$2")
                              .replace(/(\d{3})(\d{1,2})/, "$1-$2")
                              .replace(/(-\d{2})\d+?$/, "$1");
                            field.onChange(maskedValue);
                          }}
                        />
                      )}
                    />

                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <Input
                          title="Telefone"
                          isRequired
                          placeholder="(11) 91234-5678"
                          error={errors.phone?.message}
                          value={field.value}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            const maskedValue = value
                              .replace(/(\d{2})(\d)/, "($1) $2")
                              .replace(/(\d{5})(\d)/, "$1-$2")
                              .replace(/(-\d{4})\d+?$/, "$1");
                            field.onChange(maskedValue);
                          }}
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Endereço</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      title="Rua"
                      isRequired
                      placeholder="Av. Principal"
                      error={errors.street?.message}
                      {...register("street")}
                    />

                    <Input
                      title="Número"
                      isRequired
                      placeholder="123"
                      error={errors.number?.message}
                      {...register("number")}
                    />

                    <Input
                      title="Bairro"
                      isRequired
                      placeholder="Centro"
                      error={errors.neighborhood?.message}
                      {...register("neighborhood")}
                    />

                    <Input
                      title="Cidade"
                      isRequired
                      placeholder="São Paulo"
                      error={errors.city?.message}
                      {...register("city")}
                    />

                    <Input
                      title="Estado (UF)"
                      isRequired
                      placeholder="SP"
                      maxLength={2}
                      error={errors.state?.message}
                      {...register("state")}
                      onChange={(e) => {
                        e.target.value = e.target.value.toUpperCase();
                      }}
                    />

                    <Controller
                      name="zipCode"
                      control={control}
                      render={({ field }) => (
                        <Input
                          title="CEP"
                          isRequired
                          placeholder="00000-000"
                          error={errors.zipCode?.message}
                          value={field.value}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            const maskedValue = value
                              .replace(/(\d{5})(\d)/, "$1-$2")
                              .replace(/(-\d{3})\d+?$/, "$1");
                            field.onChange(maskedValue);
                          }}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/users")}
                    disabled={updateUser.isPending}
                    className="cursor-pointer"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    disabled={updateUser.isPending}
                    isLoading={updateUser.isPending}
                    className="text-white cursor-pointer"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Alterações
                  </Button>
                </div>
              </form>
            </CardContentUI>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
